import { ReadonlyVec3 } from 'gl-matrix';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTargetSeriesUid } from '~/hooks/target/useActiveEditTarget';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { focusGridPoint, focusGridRoi } from '~/viewportGrid/adapters/engine';

export interface ViewportFocus {
    /**
     * Frames every tile of the active structure set's image on an ROI's
     * contour, each tile in its own plane. Frames built content only: an
     * ROI that is empty, point-only, or hidden and never drawn moves no tile.
     * @returns Whether a tile shows the image: false when the image is not
     *   shown. True also when the ROI has no built contour to frame.
     */
    focusOnStructure: (roiNumber: number) => boolean;
    /**
     * Frames every tile of the active structure set's image on a point in
     * 3D space, each tile in its own plane.
     *
     * Typical use case: focusing on point-type ROIs (MARKER, ISOCENTER,
     * REGISTRATION).
     *
     * @param point - The 3D coordinates to focus on, in patient space (mm).
     * @returns Whether a tile shows the image: false when the image is not
     *   shown.
     */
    focusOnPoint: (point: ReadonlyVec3) => boolean;
}

/**
 * Viewport focus on the image of the active structure set, the edit
 * target. The image's Environment is resolved fresh from the registry on
 * each call, so a re-minted Environment is never stale.
 */
export default function useViewportFocus(): ViewportFocus {
    const seriesUid = useActiveEditTargetSeriesUid();
    const viewManager = useRecoilValue(viewManagerState);

    const focusOnStructure = useCallback(
        (roiNumber: number): boolean =>
            focusGridRoi(
                getDatasetEnvironmentRegistry(),
                viewManager,
                seriesUid,
                roiNumber
            ),
        [viewManager, seriesUid]
    );

    const focusOnPoint = useCallback(
        (point: ReadonlyVec3): boolean =>
            focusGridPoint(
                getDatasetEnvironmentRegistry(),
                viewManager,
                seriesUid,
                point
            ),
        [viewManager, seriesUid]
    );

    return { focusOnStructure, focusOnPoint };
}
